import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { Loader2 } from "lucide-react";

// Defer non-critical shell components to after first paint
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const OfflineIndicator = lazy(() => import("@/components/OfflineIndicator").then(m => ({ default: m.OfflineIndicator })));
const RoundInvitationListener = lazy(() => import("@/components/RoundInvitationListener").then(m => ({ default: m.RoundInvitationListener })));
import { TooltipProvider } from "@/components/ui/tooltip";

// Eagerly load the landing page for fast FCP
import Index from "./pages/Index";

// Lazy load all other routes
const NewRound = lazy(() => import("./pages/NewRound"));
const RoundPlay = lazy(() => import("./pages/RoundPlay"));
const Stats = lazy(() => import("./pages/Stats"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const GameInfo = lazy(() => import("./pages/GameInfo"));
const Auth = lazy(() => import("./pages/Auth"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient();

// Cleanup stale offline queue data on app startup
// Use 4-hour threshold to support full golf rounds
const cleanupStaleData = () => {
  const QUEUE_KEY = 'settleup_offline_queue';
  try {
    const stored = localStorage.getItem(QUEUE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const now = Date.now();
      const FOUR_HOURS = 4 * 60 * 60 * 1000; // Extended from 10 min to 4 hours
      
      // Filter out updates older than 4 hours - realistic for a full golf round
      const fresh = parsed.filter((u: any) => now - u.timestamp < FOUR_HOURS);
      
      if (fresh.length !== parsed.length) {
        console.log(`🧹 App startup: Cleared ${parsed.length - fresh.length} stale sync entries (>4 hours old)`);
      }
      
      if (fresh.length > 0) {
        localStorage.setItem(QUEUE_KEY, JSON.stringify(fresh));
      } else {
        localStorage.removeItem(QUEUE_KEY);
      }
    }
  } catch (e) {
    console.error('Failed to cleanup stale data:', e);
    localStorage.removeItem(QUEUE_KEY);
  }
};

// Handle service worker updates - force reload on new version
const handleServiceWorkerUpdate = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(registration => {
      // Check for waiting service worker and force activation
      if (registration.waiting) {
        console.log('🔄 New version available, activating...');
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      
      // Listen for new service worker installing
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('🔄 New version installed, reloading...');
              // Clear API cache before reload
              caches.delete('supabase-api-cache').then(() => {
                window.location.reload();
              });
            }
          });
        }
      });
    });
    
    // Reload page when new service worker takes control
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('🔄 New service worker active, reloading page...');
      window.location.reload();
    });
  }
};

// Run cleanup and service worker handling on module load
cleanupStaleData();
handleServiceWorkerUpdate();

// Deferred shell: loads toasters, offline indicator, etc. after first paint
const DeferredShell = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // Use requestIdleCallback or setTimeout to defer non-critical UI
    const id = typeof requestIdleCallback !== 'undefined' 
      ? (requestIdleCallback(() => setReady(true)), -1)
      : setTimeout(() => setReady(true), 100);
    return () => { if (id !== -1) clearTimeout(id as unknown as number); };
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <Toaster />
      <Sonner />
      <OfflineIndicator />
    </Suspense>
  );
};

// Inner component to use hooks
const AppContent = () => {
  // Defer non-critical hooks to after first paint
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <>
      <DeferredShell />
      {mounted && <Suspense fallback={null}><DeferredHooks /></Suspense>}
      <BrowserRouter>
        <Suspense fallback={null}><RoundInvitationListener /></Suspense>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/new-round" element={<NewRound />} />
            <Route path="/round/:code" element={<RoundPlay />} />
            <Route path="/stats" element={<Stats />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/game-info" element={<GameInfo />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
};

// Separate component for non-critical hooks to avoid blocking render
const DeferredHooks = lazy(() => import("@/components/DeferredHooks"));

const App = () => (
  <ThemeProvider defaultTheme="system" storageKey="settleup-theme">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AppContent />
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
